(() => {
  const HERO =
    '[data-hero-reveal]';

  const HEADING =
    '[data-split="heading"]';

  const BODY =
    '[data-hero-reveal-body]';

  const ITEM =
    '[data-hero-reveal-item]';

  const PAGE =
    '[data-barba="container"]';

  const WRAPPER =
    '.page-wrapper__content[data-barba="wrapper"]';

  const LOADER =
    '[data-load-wrap]';

  const RETRY_LIMIT = 40;
  const RETRY_DELAY = 100;


  /* =========================================
     SPLIT CONFIG
     ========================================= */

  const SPLIT_CONFIG = {
    lines: {
      duration: 0.9,
      stagger: 0.09,
    },

    words: {
      duration: 0.72,
      stagger: 0.055,
    },

    chars: {
      duration: 0.5,
      stagger: 0.018,
    },
  };


  const BODY_SPLIT_CONFIG = {
    duration: 0.78,
    stagger: 0.085,
  };


  /* =========================================
     ENVIRONMENT
     ========================================= */

  const reducedMotion =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  const designMode =
    document.documentElement
      .classList.contains(
        'shopify-design-mode'
      ) ||
    Boolean(
      window.Shopify &&
      window.Shopify.designMode
    );


  /* =========================================
     STATE
     ========================================= */

  const states =
    new WeakMap();

  let depsRegistered = false;

  let retryTimer = null;
  let retryCount = 0;

  let pageObserver = null;
  let loaderObserver = null;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function registerDependencies() {
    if (
      !window.gsap ||
      !window.SplitText
    ) {
      return false;
    }

    if (!depsRegistered) {
      gsap.registerPlugin(
        SplitText
      );

      depsRegistered = true;
    }

    return true;
  }


  /* =========================================
     HERO COLLECTION
     ========================================= */

  function getHeroes(
    scope = document
  ) {
    if (!scope) {
      return [];
    }

    const heroes = [];

    if (
      scope instanceof Element &&
      scope.matches(HERO)
    ) {
      heroes.push(scope);
    }

    if (
      typeof scope.querySelectorAll ===
        'function'
    ) {
      heroes.push(
        ...scope.querySelectorAll(
          HERO
        )
      );
    }

    return Array.from(
      new Set(heroes)
    );
  }


  function getCurrentPage() {
    return (
      document.querySelector(
        PAGE
      ) ||
      document.body
    );
  }


  /* =========================================
     HERO STATE
     ========================================= */

  function getState(hero) {
    let state =
      states.get(hero);

    if (state) {
      return state;
    }

    state = {
      hero,

      heading: null,
      split: null,
      headingTween: null,

      body: null,
      bodySplit: null,
      bodyTween: null,
      bodyOpacity: 1,

      master: null,

      items: [],
      itemConfigs: [],

      staged: false,
      prepared: false,
      preparing: false,

      playing: false,
      played: false,
      playRequested: false,

      destroyed: false,
    };

    states.set(
      hero,
      state
    );

    return state;
  }


  /* =========================================
     STATIC / REDUCED MOTION
     ========================================= */

  function showStatic(state) {
    if (!state) {
      return;
    }

    state.master?.kill();

    state.headingTween?.kill();
    state.bodyTween?.kill();

    state.split?.revert();
    state.bodySplit?.revert();

    state.master = null;

    state.headingTween = null;
    state.bodyTween = null;

    state.split = null;
    state.bodySplit = null;

    const targets = [
      state.heading,
      state.body,
      ...state.items,
    ].filter(Boolean);

    if (
      window.gsap &&
      targets.length
    ) {
      gsap.set(
        targets,
        {
          clearProps:
            'transform,opacity,visibility',
        }
      );
    }

    state.prepared = true;
    state.preparing = false;

    state.playing = false;
    state.played = true;
    state.playRequested = false;
  }


  /* =========================================
     LIGHTWEIGHT BARBA STAGING

     Barba inserts the incoming page while the
     transition panel is moving. Only hide hero
     content at that point. SplitText and layout
     measurement wait until wambolt:page:enter.
     ========================================= */

  function stageHero(
    hero
  ) {
    if (
      !hero ||
      !(hero instanceof Element)
    ) {
      return;
    }


    const state =
      getState(hero);


    if (
      state.staged ||
      state.prepared ||
      state.preparing ||
      state.destroyed
    ) {
      return;
    }


    state.heading =
      hero.querySelector(
        HEADING
      );


    state.body =
      hero.querySelector(
        BODY
      );


    state.items =
      Array.from(
        hero.querySelectorAll(
          ITEM
        )
      );


    const targets = [
      state.heading,
      state.body,
      ...state.items,
    ].filter(Boolean);


    targets.forEach(
      (target) => {
        target.style.visibility =
          'hidden';
      }
    );


    state.staged = true;
  }


  function stageScope(
    scope = document
  ) {
    getHeroes(scope)
      .forEach(
        stageHero
      );
  }


  /* =========================================
     IMMEDIATE PREP

     Hide incoming hero content synchronously
     before any font waiting or SplitText work.
     ========================================= */

  function prepareInitialElements(
    state
  ) {
    state.heading =
      state.hero.querySelector(
        HEADING
      );


    state.body =
      state.hero.querySelector(
        BODY
      );


    state.items =
      Array.from(
        state.hero.querySelectorAll(
          ITEM
        )
      );


    if (state.body) {
      const computedOpacity =
        Number.parseFloat(
          getComputedStyle(
            state.body
          ).opacity
        );


      state.bodyOpacity =
        Number.isFinite(
          computedOpacity
        )
          ? computedOpacity
          : 1;
    }


    prepareItems(
      state
    );


    if (state.heading) {
      gsap.set(
        state.heading,
        {
          autoAlpha: 0,
        }
      );
    }


    if (state.body) {
      gsap.set(
        state.body,
        {
          autoAlpha: 0,
        }
      );
    }
  }


  /* =========================================
     SPLIT HEADING
     ========================================= */

  function createHeadingSplit(
    state
  ) {
    const heading =
      state.heading;

    if (!heading) {
      return;
    }


    const requested =
      heading.dataset.splitReveal ||
      'lines';


    const type =
      Object.prototype
        .hasOwnProperty
        .call(
          SPLIT_CONFIG,
          requested
        )
        ? requested
        : 'lines';


    const splitTypes =
      type === 'lines'
        ? [
            'lines',
          ]
        : type === 'words'
          ? [
              'lines',
              'words',
            ]
          : [
              'lines',
              'words',
              'chars',
            ];


    const config =
      SPLIT_CONFIG[type];


    state.split =
      SplitText.create(
        heading,
        {
          type:
            splitTypes.join(
              ', '
            ),

          mask:
            'lines',

          autoSplit:
            true,

          linesClass:
            'wambolt-hero-reveal__line',

          wordsClass:
            'wambolt-hero-reveal__word',

          charsClass:
            'wambolt-hero-reveal__char',


          onSplit(instance) {
            const targets =
              instance[type];


            if (
              state.destroyed ||
              state.played ||
              reducedMotion.matches
            ) {
              gsap.set(
                heading,
                {
                  autoAlpha: 1,
                }
              );


              gsap.set(
                targets,
                {
                  clearProps:
                    'transform,opacity,visibility',
                }
              );


              state.headingTween =
                null;


              return undefined;
            }


            gsap.set(
              targets,
              {
                yPercent: 110,
              }
            );


            gsap.set(
              heading,
              {
                autoAlpha: 1,
              }
            );


            state.headingTween =
              gsap.to(
                targets,
                {
                  yPercent: 0,

                  duration:
                    config.duration,

                  stagger:
                    config.stagger,

                  ease:
                    'expo.out',

                  paused:
                    true,
                }
              );


            return (
              state.headingTween
            );
          },
        }
      );
  }


  /* =========================================
     SPLIT BODY
     ========================================= */

  function createBodySplit(
    state
  ) {
    const body =
      state.body;

    if (!body) {
      return;
    }


    state.bodySplit =
      SplitText.create(
        body,
        {
          type:
            'lines',

          mask:
            'lines',

          autoSplit:
            true,

          linesClass:
            'wambolt-hero-reveal__body-line',


          onSplit(instance) {
            const lines =
              instance.lines;


            if (
              state.destroyed ||
              state.played ||
              reducedMotion.matches
            ) {
              gsap.set(
                body,
                {
                  autoAlpha:
                    state.bodyOpacity,
                }
              );


              gsap.set(
                lines,
                {
                  clearProps:
                    'transform,opacity,visibility',
                }
              );


              state.bodyTween =
                null;


              return undefined;
            }


            gsap.set(
              lines,
              {
                yPercent: 110,
              }
            );


            gsap.set(
              body,
              {
                autoAlpha:
                  state.bodyOpacity,
              }
            );


            state.bodyTween =
              gsap.to(
                lines,
                {
                  yPercent: 0,

                  duration:
                    BODY_SPLIT_CONFIG
                      .duration,

                  stagger:
                    BODY_SPLIT_CONFIG
                      .stagger,

                  ease:
                    'expo.out',

                  paused:
                    true,
                }
              );


            return (
              state.bodyTween
            );
          },
        }
      );
  }


  /* =========================================
     SUPPORTING ITEMS
     ========================================= */

  function prepareItems(
    state
  ) {
    state.itemConfigs =
      state.items.map(
        (element) => {
          const role =
            element.dataset
              .heroRevealItem ||
            '';


          const isAction =
            role ===
              'primary-action' ||
            role ===
              'secondary-action' ||
            role ===
              'demo-action' ||
            role ===
              'scroll-action';


          const y =
            role === 'eyebrow'
              ? 10
              : role === 'footer'
                ? 14
                : role === 'kicker'
                  ? 12
                  : isAction
                    ? 12
                    : 18;


          const computedOpacity =
            Number.parseFloat(
              getComputedStyle(
                element
              ).opacity
            );


          const opacity =
            Number.isFinite(
              computedOpacity
            )
              ? computedOpacity
              : 1;


          gsap.set(
            element,
            {
              autoAlpha: 0,
              y,
            }
          );


          return {
            element,
            role,
            opacity,
          };
        }
      );
  }


  /* =========================================
     PREPARE HERO
     ========================================= */

  async function prepareHero(
    hero
  ) {
    if (
      !hero ||
      !(hero instanceof Element)
    ) {
      return;
    }


    const state =
      getState(hero);


    if (
      state.prepared ||
      state.preparing ||
      state.destroyed
    ) {
      return;
    }


    state.preparing = true;


    /* =========================================
       DESIGN MODE / REDUCED MOTION
       ========================================= */

    if (
      designMode ||
      reducedMotion.matches
    ) {
      state.heading =
        hero.querySelector(
          HEADING
        );


      state.body =
        hero.querySelector(
          BODY
        );


      state.items =
        Array.from(
          hero.querySelectorAll(
            ITEM
          )
        );


      state.preparing = false;


      showStatic(
        state
      );


      return;
    }


    /* =========================================
       DEPENDENCIES
       ========================================= */

    if (
      !registerDependencies()
    ) {
      state.preparing = false;

      return;
    }


    /* =========================================
       PRE-HIDE

       This happens before awaiting fonts.
       ========================================= */

    prepareInitialElements(
      state
    );


    /* =========================================
       FONTS
       ========================================= */

    try {
      if (
        document.fonts?.ready
      ) {
        await document.fonts.ready;
      }
    } catch (error) {
    }


    if (
      state.destroyed ||
      !hero.isConnected
    ) {
      state.preparing = false;

      return;
    }


    /* =========================================
       SPLIT
       ========================================= */

    createHeadingSplit(
      state
    );


    createBodySplit(
      state
    );


    state.prepared = true;
    state.preparing = false;


    /* =========================================
       DEFERRED PLAY
       ========================================= */

    if (
      state.playRequested
    ) {
      playPreparedHero(
        state
      );
    }
  }


  function prepareScope(
    scope = document
  ) {
    return Promise.all(
      getHeroes(scope)
        .map(
          prepareHero
        )
    );
  }


  /* =========================================
     PLAY PREPARED HERO
     ========================================= */

  function playPreparedHero(
    state
  ) {
    if (
      !state ||
      state.destroyed ||
      state.playing ||
      state.played
    ) {
      return;
    }


    if (
      designMode ||
      reducedMotion.matches
    ) {
      showStatic(
        state
      );

      return;
    }


    if (
      !state.prepared
    ) {
      state.playRequested = true;

      return;
    }


    state.playRequested = false;
    state.playing = true;


    /* =========================================
       SUPPORTING ITEM LOOKUP
       ========================================= */

    const getItem =
      (role) =>
        state.itemConfigs
          .find(
            (config) =>
              config.role === role
          ) ||
        null;


    const eyebrow =
      getItem(
        'eyebrow'
      );


    const body =
      getItem(
        'body'
      );


    const footer =
      getItem(
        'footer'
      );


    const primaryAction =
      getItem(
        'primary-action'
      );


    const secondaryAction =
      getItem(
        'secondary-action'
      );


    const demoAction =
      getItem(
        'demo-action'
      );


    const kicker =
      getItem(
        'kicker'
      );


    const scrollAction =
      getItem(
        'scroll-action'
      );


    const hasSplitBody =
      Boolean(
        state.body
      );


    /* =========================================
       MASTER TIMELINE
       ========================================= */

    state.master =
      gsap.timeline({
        paused: true,

        defaults: {
          overwrite:
            'auto',
        },

        onComplete() {
          state.playing = false;
          state.played = true;


          const clearTargets =
            Array.from(
              new Set(
                [
                  eyebrow?.element,
                  body?.element,
                  footer?.element,
                  primaryAction?.element,
                  secondaryAction?.element,
                  demoAction?.element,
                  kicker?.element,
                  scrollAction?.element,
                ].filter(Boolean)
              )
            );


          if (
            clearTargets.length
          ) {
            gsap.set(
              clearTargets,
              {
                clearProps:
                  'transform,opacity,visibility',
              }
            );
          }


          if (
            state.heading
          ) {
            gsap.set(
              state.heading,
              {
                clearProps:
                  'opacity,visibility',
              }
            );
          }


          if (
            state.body
          ) {
            gsap.set(
              state.body,
              {
                clearProps:
                  'opacity,visibility',
              }
            );
          }
        },
      });


    /* =========================================
       EYEBROW
       ========================================= */

    if (eyebrow) {
      state.master.to(
        eyebrow.element,
        {
          autoAlpha:
            eyebrow.opacity,

          y: 0,

          duration:
            0.45,

          ease:
            'power3.out',
        },
        0
      );
    }


    /* =========================================
       ENHANCED HERO
       ========================================= */

    if (hasSplitBody) {

      const headingStart =
        eyebrow
          ? 0.14
          : 0;


      const bodyStart =
        state.headingTween
          ? headingStart + 0.34
          : eyebrow
            ? 0.24
            : 0;


      const tailStart =
        state.bodyTween
          ? bodyStart + 0.36
          : state.headingTween
            ? headingStart + 0.48
            : eyebrow
              ? 0.34
              : 0;


      /* =======================================
         HEADING
         ======================================= */

      if (
        state.headingTween
      ) {
        state.master.add(
          state.headingTween,
          headingStart
        );


        state.headingTween
          .paused(false);
      }


      /* =======================================
         BODY
         ======================================= */

      if (
        state.bodyTween
      ) {
        state.master.add(
          state.bodyTween,
          bodyStart
        );


        state.bodyTween
          .paused(false);
      }


      /* =======================================
         SUPPORTING ITEMS
         ======================================= */

      const tailItems =
        [
          primaryAction,
          secondaryAction,
          demoAction,
          kicker,
          scrollAction,
        ].filter(Boolean);


      tailItems.forEach(
        (
          item,
          index
        ) => {
          state.master.to(
            item.element,
            {
              autoAlpha:
                item.opacity,

              y: 0,

              duration:
                0.5,

              ease:
                'power3.out',
            },

            tailStart +
              (
                index *
                0.1
              )
          );
        }
      );


    } else {

      /* =======================================
         LEGACY HERO BEHAVIOR
         ======================================= */

      if (
        state.headingTween
      ) {
        state.master.add(
          state.headingTween,
          0.06
        );


        state.headingTween
          .paused(false);
      }


      if (body) {
        state.master.to(
          body.element,
          {
            autoAlpha:
              body.opacity,

            y: 0,

            duration:
              0.65,

            ease:
              'power3.out',
          },
          0.3
        );
      }


      if (footer) {
        state.master.to(
          footer.element,
          {
            autoAlpha:
              footer.opacity,

            y: 0,

            duration:
              0.6,

            ease:
              'power3.out',
          },
          0.46
        );
      }
    }


    state.master.play(0);
  }


  function playHero(
    hero
  ) {
    const state =
      getState(hero);


    if (
      !state.prepared
    ) {
      state.playRequested =
        true;


      prepareHero(
        hero
      );


      return;
    }


    playPreparedHero(
      state
    );
  }


  function playScope(
    scope = document
  ) {
    getHeroes(scope)
      .forEach(
        playHero
      );
  }


  /* =========================================
     DESTROY
     ========================================= */

  function destroyHero(
    hero
  ) {
    const state =
      states.get(hero);


    if (!state) {
      return;
    }


    state.destroyed = true;


    state.master?.kill();

    state.headingTween?.kill();
    state.bodyTween?.kill();

    state.split?.revert();
    state.bodySplit?.revert();


    const targets = [
      state.heading,
      state.body,
      ...state.items,
    ].filter(Boolean);


    if (
      window.gsap &&
      targets.length
    ) {
      gsap.set(
        targets,
        {
          clearProps:
            'transform,opacity,visibility',
        }
      );
    }


    states.delete(
      hero
    );
  }


  function destroyScope(
    scope = document
  ) {
    getHeroes(scope)
      .forEach(
        destroyHero
      );
  }


  /* =========================================
     STAGE INCOMING BARBA PAGES

     Keep this observer intentionally lightweight.
     Full SplitText preparation during this observer
     competes with the page-transition panel.
     ========================================= */

  function observeIncomingPages() {
    if (pageObserver) {
      return;
    }


    const wrapper =
      document.querySelector(
        WRAPPER
      );


    if (!wrapper) {
      return;
    }


    pageObserver =
      new MutationObserver(
        (mutations) => {
          mutations.forEach(
            (mutation) => {
              mutation.addedNodes
                .forEach(
                  (node) => {
                    if (
                      !(
                        node instanceof
                        Element
                      )
                    ) {
                      return;
                    }


                    if (
                      node.matches(
                        PAGE
                      )
                    ) {
                      stageScope(
                        node
                      );

                      return;
                    }


                    const incoming =
                      node
                        .querySelector?.(
                          PAGE
                        );


                    if (
                      incoming
                    ) {
                      stageScope(
                        incoming
                      );
                    }
                  }
                );
            }
          );
        }
      );


    pageObserver.observe(
      wrapper,
      {
        childList: true,
        subtree: false,
      }
    );
  }


  /* =========================================
     INITIAL LOADER HANDOFF
     ========================================= */

  function loaderIsPresent() {
    return Boolean(
      document.querySelector(
        LOADER
      )
    );
  }


  function playInitialPageWhenReady() {
    if (
      designMode ||
      reducedMotion.matches
    ) {
      playScope(
        getCurrentPage()
      );

      return;
    }


    if (
      !loaderIsPresent()
    ) {
      requestAnimationFrame(
        () => {
          playScope(
            getCurrentPage()
          );
        }
      );

      return;
    }


    if (loaderObserver) {
      return;
    }


    loaderObserver =
      new MutationObserver(
        () => {
          if (
            loaderIsPresent()
          ) {
            return;
          }


          loaderObserver
            .disconnect();


          loaderObserver =
            null;


          requestAnimationFrame(
            () => {
              playScope(
                getCurrentPage()
              );
            }
          );
        }
      );


    loaderObserver.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true,
      }
    );
  }


  /* =========================================
     DEPENDENCY-SAFE START
     ========================================= */

  function clearRetry() {
    if (
      retryTimer === null
    ) {
      return;
    }


    clearTimeout(
      retryTimer
    );


    retryTimer = null;
  }


  function scheduleRetry() {
    if (
      retryTimer !== null
    ) {
      return;
    }


    if (
      retryCount >=
      RETRY_LIMIT
    ) {
      console.warn(
        'Wambolt hero reveal could not initialize because GSAP or SplitText is unavailable.'
      );


      getHeroes(document)
        .forEach(
          (hero) => {
            const state =
              getState(hero);


            state.heading =
              hero.querySelector(
                HEADING
              );


            state.body =
              hero.querySelector(
                BODY
              );


            state.items =
              Array.from(
                hero.querySelectorAll(
                  ITEM
                )
              );


            showStatic(
              state
            );
          }
        );


      return;
    }


    retryCount += 1;


    retryTimer =
      setTimeout(
        () => {
          retryTimer = null;

          start();
        },
        RETRY_DELAY
      );
  }


  function start() {
    if (
      !registerDependencies()
    ) {
      scheduleRetry();

      return;
    }


    retryCount = 0;


    clearRetry();


    observeIncomingPages();


    const currentPage =
      getCurrentPage();


    /*
     * Preserve the original stable initial-load order:
     * fully prepare the hero before it ever plays.
     *
     * The site-loader runtime waits for this signal
     * before beginning its visible animation, keeping
     * SplitText/font/layout work out of the loader's
     * moving frames without pushing hero layout changes
     * later than Lenis expects.
     */
    prepareScope(
      currentPage
    ).finally(
      () => {
        window.wamboltHeroInitialPrepared =
          true;

        window.dispatchEvent(
          new CustomEvent(
            'wambolt:hero:initial-prepared',
            {
              detail: {
                container:
                  currentPage,
              },
            }
          )
        );
      }
    );


    playInitialPageWhenReady();
  }


  /* =========================================
     WAMBOLT / BARBA LIFECYCLE
     ========================================= */

  window.addEventListener(
    'wambolt:page:before-leave',
    (event) => {
      if (
        event.detail?.container
      ) {
        destroyScope(
          event.detail.container
        );
      }
    }
  );


  window.addEventListener(
    'wambolt:page:enter',
    (event) => {
      const container =
        event.detail?.container ||
        getCurrentPage();


      /*
       * prepareScope begins synchronously and hides
       * unsplit hero content before its first await.
       */
      prepareScope(
        container
      );


      /*
       * The page-transition runtime dispatches this
       * event immediately before it releases its
       * transition lock and restarts Lenis.
       *
       * Waiting one frame means the scroll container
       * has returned to its normal layout state before
       * the hero timeline advances.
       */
      requestAnimationFrame(
        () => {
          if (
            container instanceof Element &&
            !container.isConnected
          ) {
            return;
          }


          if (
            !loaderIsPresent()
          ) {
            playScope(
              container
            );
          }
        }
      );
    }
  );


  /* =========================================
     SHOPIFY THEME EDITOR
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      prepareScope(
        event.target
      );


      if (designMode) {
        playScope(
          event.target
        );
      }
    }
  );


  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyScope(
        event.target
      );
    }
  );


  /* =========================================
     REDUCED MOTION
     ========================================= */

  function handleReducedMotionChange() {
    getHeroes(
      getCurrentPage()
    ).forEach(
      (hero) => {
        const state =
          states.get(hero);


        if (state) {
          showStatic(
            state
          );
        }
      }
    );
  }


  if (
    typeof reducedMotion
      .addEventListener ===
    'function'
  ) {
    reducedMotion.addEventListener(
      'change',
      handleReducedMotionChange
    );
  } else if (
    typeof reducedMotion
      .addListener ===
    'function'
  ) {
    reducedMotion.addListener(
      'change',
      handleReducedMotionChange
    );
  }


  /* =========================================
     INITIAL LOAD
     ========================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }
})();